import { makeAutoObservable } from 'mobx'
import qs from 'qs'
import { generatePath } from 'react-router'

import { teacherController } from '@/controller/teacher.controller'
import type { GroupInfo } from '@/dto/common/GroupInfo'
import type { MakeLessonMaterialCommonParams } from '@/dto/LessonMaterial/MakeLessonMaterialCommonParams'
import type { TeacherFeatureKey } from '@/models/(Permission)/FeaturePermission'
import { CheckableColl } from '@/models/CheckableColl'
import CheckableMap from '@/models/CheckableMap'
import type LessonWorksheetGroupService from '@/page/Lesson/Worksheet/WorksheetGroup/_service/LessonWorksheetGroup.service'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router/RouteName'
import { remoteStorageService } from '@/utils/axios/remoteStorage'

// 지금은 B2G에서만 사용
export class QuickMenuService {
  // 기본 목록은 FE에서 관리, 이유 : FE에서 라우트 주소나, 메뉴 이름등을 바꿀때 BE와 의존성이 생기므로
  // BE에 id를 기반으로 저장하므로 id는 변경되면 안됨
  // 추후에 정규화하여 기본목록을 BE로 관리한다면 id 체계에 따라 일괄 DB 업데이트 등 필요
  private readonly QUICK_MENU_ITEMS = [
    {
      id: 1,
      menu: '학습지 만들기',
      subMenu: '단원유형별',
      iconName: 'add_circle',
      route: (
        params: Pick<
          MakeLessonMaterialCommonParams<'NONE', '단원유형별'>['entryContext'],
          'entryPath' | 'destinationPath'
        >,
      ) => {
        commonRepo.router.push({
          pathname: generatePath(RouteName.makeWorksheetByChapter),
          search: qs.stringify(params),
        })
      },
    },
    {
      id: 2,
      menu: '학습지 만들기',
      subMenu: '시중교재',
      iconName: 'add_circle',
      route: (
        params: Pick<
          MakeLessonMaterialCommonParams<'NONE', '시중교재'>['entryContext'],
          'entryPath' | 'destinationPath'
        >,
      ) => {
        commonRepo.router.push({
          pathname: generatePath(RouteName.makeWorksheetByWorkbook),
          search: qs.stringify(params),
        })
      },
    },
    {
      id: 3,
      menu: '학습지 만들기',
      subMenu: '수능/모의고사',
      iconName: 'add_circle',
      route: (
        params: Pick<
          MakeLessonMaterialCommonParams<'NONE', '수능모의고사'>['entryContext'],
          'entryPath' | 'destinationPath'
        >,
      ) => {
        commonRepo.router.push({
          pathname: generatePath(RouteName.makeWorksheetByPracticeTest),
          search: qs.stringify(params),
        })
      },
    },
    {
      id: 4,
      featureKey: 'CONTENT_SIGNATURE_WORKBOOK' as TeacherFeatureKey,
      menu: '학습지 만들기',
      subMenu: '시그니처 교재',
      iconName: 'add_circle',
      route: (
        params: Pick<
          MakeLessonMaterialCommonParams<'NONE', '시그니처교재'>['entryContext'],
          'entryPath' | 'destinationPath'
        >,
      ) => {
        commonRepo.router.push({
          pathname: generatePath(RouteName.makeWorksheetBySignatureWorkbook),
          search: qs.stringify(params),
        })
      },
    },
    {
      id: 5,
      menu: '내학습지 목록',
      subMenu: '수업준비 → 학습지',
      iconName: 'description_paper',
      route: () => {
        commonRepo.router.push(RouteName.academyShareScopeWorksheet)
      },
    },
    {
      id: 6,
      menu: '그룹 학습지 목록',
      subMenu: '수업 → 학습지 목록',
      iconName: 'list',
      route: (groupInfo: GroupInfo) => {
        const state: typeof LessonWorksheetGroupService.PATH_STATE = {
          viewType: 'list',
        }

        if (groupInfo.data.type === 'grade') {
          commonRepo.router.push({
            pathname: generatePath(RouteName.lessonWorksheetGrade, {
              gradeKey: groupInfo.key,
            }),
            state,
          })
        }
        if (groupInfo.data.type === 'lesson-class') {
          commonRepo.router.push({
            pathname: generatePath(RouteName.lessonWorksheetClass, {
              lessonClassId: groupInfo.key,
            }),
            state,
          })
        }
      },
    },
    {
      id: 7,
      menu: '그룹 학습지 현황',
      subMenu: '수업 → 학습지 현황',
      iconName: 'navigation',
      route: (groupInfo: GroupInfo) => {
        const state: typeof LessonWorksheetGroupService.PATH_STATE = {
          viewType: 'board',
        }
        if (groupInfo.data.type === 'grade') {
          commonRepo.router.push({
            pathname: generatePath(RouteName.lessonWorksheetGrade, {
              gradeKey: groupInfo.key,
            }),
            state,
          })
        }
        if (groupInfo.data.type === 'lesson-class') {
          commonRepo.router.push({
            pathname: generatePath(RouteName.lessonWorksheetClass, {
              lessonClassId: groupInfo.key,
            }),
            state,
          })
        }
      },
    },
    {
      id: 8,
      menu: '유형분석',
      subMenu: '수업 → 유형분석',
      iconName: 'smile',
      route: (groupInfo: GroupInfo, studentId?: string) => {
        if (studentId) {
          commonRepo.router.push(
            generatePath(RouteName.achievementAnalysisByStudent, {
              studentId,
            }),
          )
          return
        }
        if (groupInfo.data.type === 'grade') {
          commonRepo.router.push(
            generatePath(RouteName.achievementAnalysisByGrade, {
              gradeKey: groupInfo.key,
            }),
          )
        }
        if (groupInfo.data.type === 'lesson-class') {
          commonRepo.router.push(
            generatePath(RouteName.achievementAnalysisClass, {
              lessonClassId: groupInfo.key,
            }),
          )
        }
      },
    },
    {
      id: 9,
      menu: '학습내역',
      subMenu: '수업 → 학습내역',
      iconName: 'answer_check',
      route: (groupInfo: GroupInfo) => {
        if (groupInfo.data.type === 'grade') {
          commonRepo.router.push(
            generatePath(RouteName.lessonLearningHistoryGrade, {
              gradeKey: groupInfo.key,
            }),
          )
        }
        if (groupInfo.data.type === 'lesson-class') {
          commonRepo.router.push(
            generatePath(RouteName.lessonLearningHistoryClass, {
              lessonClassId: groupInfo.key,
            }),
          )
        }
      },
    },
    {
      id: 10,
      menu: '보고서',
      subMenu: '수업 → 보고서',
      iconName: 'book_opened',
      route: (groupInfo: GroupInfo) => {
        if (groupInfo.data.type === 'grade') {
          commonRepo.router.push(
            generatePath(RouteName.lessonReportGrade, {
              gradeKey: groupInfo.key,
            }),
          )
        }
        if (groupInfo.data.type === 'lesson-class') {
          commonRepo.router.push(
            generatePath(RouteName.lessonReportClass, {
              lessonClassId: groupInfo.key,
            }),
          )
        }
      },
    },
  ] as const

  private _isReady = false
  get isReady() {
    return this._isReady
  }
  private _fromQuickMenu = false
  private _teacherSetting: res.TeacherSetting | null = null
  private _useInitialQuickMenuSetting: boolean = false

  quickMenuColl: CheckableColl<QuickMenuItem> = new CheckableColl(
    CheckableMap.fromArr(Array.from(this.QUICK_MENU_ITEMS), 'id' as keyof QuickMenuItem),
  )

  get availableQuickMenuItems() {
    return this.QUICK_MENU_ITEMS.filter((item) => {
      if (!('featureKey' in item)) return true
      return commonRepo.permission.byFeature.isAllowed(item.featureKey)
    })
  }

  constructor() {
    makeAutoObservable(this)
  }
  // 초기화
  init = async () => {
    await this._getRemoteStorageIsInitialQuickMenuSetting()
    await this._fetchQuickMenuSetting()
    this._isReady = true
  }

  get fromQuickMenu() {
    return this._fromQuickMenu
  }

  set fromQuickMenu(fromQuickMenu: boolean) {
    this._fromQuickMenu = fromQuickMenu
  }

  get activeQuickMenus() {
    const checkedValues = this.quickMenuColl.toCheckedValues
    return this.availableQuickMenuItems.filter((item) =>
      checkedValues.some((checked) => checked.id === item.id),
    )
  }

  private _getRemoteStorageIsInitialQuickMenuSetting = async () => {
    const response = await remoteStorageService.get('USE_INITIAL_QUICK_MENU_SETTING')
    if (response?.USE_INITIAL_QUICK_MENU_SETTING === false) {
      this._useInitialQuickMenuSetting = false
    } else {
      this._useInitialQuickMenuSetting = true
    }
  }

  private _saveIsInitialQuickMenuSettingToRemoteStorage = async () => {
    await remoteStorageService.put('USE_INITIAL_QUICK_MENU_SETTING', false)
  }

  private _fetchQuickMenuSetting = async () => {
    const teacherSetting = await teacherController.fetchTeacherLabSetting()
    this._teacherSetting = teacherSetting
    if (
      !teacherSetting.quickMenuSetting ||
      !teacherSetting.quickMenuSetting?.quickMenuIds?.length
    ) {
      if (this._useInitialQuickMenuSetting) {
        const defaultQuickMenuIds = [1, 5, 7, 8]
        defaultQuickMenuIds.forEach((id) => {
          this.quickMenuColl.setCheck(id as QuickMenuItem['id'])
        })
        return
      }
      return
    }

    teacherSetting.quickMenuSetting.quickMenuIds.forEach((id: number) => {
      this.quickMenuColl.setCheck(id as QuickMenuItem['id'])
    })
  }
  private _sortByOrder = () => {
    const checkedValues = this.quickMenuColl.toCheckedValues
    return this.availableQuickMenuItems
      .filter((item) => checkedValues.some((checked) => checked.id === item.id))
      .map((item) => item.id)
  }
  saveQuickMenuSetting = async () => {
    if (!this._teacherSetting) return
    const sortedIds = this._sortByOrder()
    this._saveIsInitialQuickMenuSettingToRemoteStorage()
    await teacherController.updateTeacherSetting({
      ...this._teacherSetting,
      quickMenuSetting: {
        quickMenuIds: sortedIds,
      },
    })
  }
}

export const quickMenuService = new QuickMenuService()

export type QuickMenuItem = QuickMenuService['availableQuickMenuItems'][number]
